import React, { useState } from "react";
import {
    TextField,
    Button,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Typography,
    CircularProgress,
    Grid,
} from "@mui/material";
import {
    CheckOutlined,
    CloseOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";

export const HTTP_METHODS = ["GET", "POST", "PUT", "DELETE", "PATCH"];

export const METHOD_COLORS = {
    GET: "#22c55e",
    POST: "#3b82f6",
    PUT: "#f59e0b",
    DELETE: "#ef4444",
    PATCH: "#8b5cf6",
};

const EndpointForm = ({ endpoint, onSave, onCancel, isSaving }) => {
    const [form, setForm] = useState({
        name: endpoint?.name || "",
        method: endpoint?.method || "POST",
        path: endpoint?.path || "",
        bodyTemplate: endpoint?.bodyTemplate
            ? JSON.stringify(endpoint.bodyTemplate, null, 2)
            : "",
        description: endpoint?.description || "",
        isActive: endpoint?.isActive !== false,
    });

    const updateForm = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => {
        if (!form.name.trim() || !form.path.trim()) {
            toast.error("Name and Path are required");
            return;
        }

        let parsedBody = null;
        if (form.bodyTemplate.trim()) {
            try {
                parsedBody = JSON.parse(form.bodyTemplate);
            } catch {
                toast.error("Invalid JSON in Body Template");
                return;
            }
        }

        onSave({
            ...form,
            bodyTemplate: parsedBody,
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                mb: 2,
            }}
        >
            <div className="space-y-4">
                {/* Name / Method / Path */}
                <div className="grid grid-cols-3 gap-4">
                    <TextField
                        size="small"
                        label="Endpoint Name"
                        value={form.name}
                        onChange={(e) => updateForm("name", e.target.value)}
                        placeholder="e.g., Create Invoice"
                        required
                    />
                    <FormControl size="small">
                        <InputLabel>Method</InputLabel>
                        <Select
                            value={form.method}
                            label="Method"
                            onChange={(e) => updateForm("method", e.target.value)}
                        >
                            {HTTP_METHODS.map((method) => (
                                <MenuItem key={method} value={method}>
                                    <Chip
                                        label={method}
                                        size="small"
                                        sx={{
                                            bgcolor: METHOD_COLORS[method],
                                            color: "white",
                                            fontWeight: "bold",
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        label="Path"
                        value={form.path}
                        onChange={(e) => updateForm("path", e.target.value)}
                        placeholder="e.g., /invoices"
                        required
                    />
                </div>

                {/* Description */}
                <TextField
                    size="small"
                    label="Description"
                    value={form.description}
                    onChange={(e) => updateForm("description", e.target.value)}
                    placeholder="Describe this endpoint..."
                    fullWidth
                    multiline
                    rows={3}
                />

                {/* Body Template */}
                <div>
                    <Typography variant="subtitle2" className="mb-2 font-semibold">
                        Body Template (JSON)
                    </Typography>
                    <TextField
                        size="small"
                        multiline
                        rows={6}
                        value={form.bodyTemplate}
                        onChange={(e) => updateForm("bodyTemplate", e.target.value)}
                        placeholder='{"key": "value"}'
                        fullWidth
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    {onCancel && (
                        <Button
                            variant="outlined"
                            onClick={onCancel}
                            startIcon={<CloseOutlined />}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving}
                        startIcon={
                            isSaving ? <CircularProgress size={16} /> : <CheckOutlined />
                        }
                    >
                        Save Endpoint
                    </Button>
                </div>
            </div>
        </Paper>
    );
};

export default EndpointForm;